import { useNumericInput } from "./useNumericInput";



export interface NumberInputProps {
  value: number;
  onChange: (value: number) => void;
  placeholder?: string;
  /** Shown while the field is required and left empty on blur. */
  errorMessage?: string;
  required?: boolean;
  max?: number;
  /** Value to fall back to on blur when empty and not required. Defaults to 0. */
  emptyFallback?: number;
  /** Small trailing label, e.g. "%". */
  suffix?: string;
  align?: "left" | "right";
  disabled?: boolean;
  id?: string;
  className?: string;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  "aria-label"?: string;
}

const groupInteger = (n: number) => new Intl.NumberFormat("id-ID").format(n);
const digitsOnly = (raw: string) => raw.replace(/\D/g, "");

/**
 * Plain numeric input — thousand-separated, empty-while-editing, never
 * forces "0" as the user types. Use CurrencyInput instead for money fields.
 */
export default function NumberInput({
  value,
  onChange,
  placeholder,
  errorMessage = "This field is required.",
  required = false,
  max,
  emptyFallback = 0,
  suffix,
  align = "right",
  disabled = false,
  id,
  className = "",
  onKeyDown,
  ...aria
}: NumberInputProps) {
  const { displayValue, inputRef, handleChange, handleFocus, handleBlur, isInvalid } =
    useNumericInput({
      value,
      onChange,
      format: groupInteger,
      parseDigits: digitsOnly,
      required,
      max,
      emptyFallback,
    });

  return (
    <div className="relative">
      <input
        ref={inputRef}
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={displayValue}
        placeholder={placeholder}
        disabled={disabled}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        aria-invalid={isInvalid || undefined}
        className={[
          "w-full rounded-md border bg-transparent px-2.5 py-2 text-sm tabular-nums text-gray-800 outline-none transition-colors",
          "placeholder:text-gray-400 hover:border-gray-200 focus:bg-white focus:ring-2",
          align === "right" ? "text-right" : "text-left",
          suffix ? "pr-6" : "",
          isInvalid
            ? "border-red-300 focus:border-red-400 focus:ring-red-100"
            : "border-transparent focus:border-blue-400 focus:ring-blue-100",
          className,
        ].join(" ")}
        {...aria}
      />
      {suffix && (
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">
          {suffix}
        </span>
      )}
      {isInvalid && (
        <p className="absolute left-0 top-full mt-0.5 whitespace-nowrap text-xs text-red-500">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
